import { useRequireDelivery } from '~/src/common/components/ProductListItem/useRequireDelivery';
import { UpdateCartProduct } from '~/src/common/hooks/cart/types';
import { useUpdateCartProductQuantity } from '~/src/common/hooks/cart/useUpdateCartProductQuantity';
import { useHandleFirstAddToCartError } from '~/src/common/services/error-handler/error-handler';
import {
  AddToCartProductEventSource,
  ExtraCartEventProperties,
  getCartEventMapping,
} from '~/src/common/services/Tracker';
import Tracker from '~/src/common/services/Tracker/Tracker';
import { getCartQuantity } from '~/src/common/utils/cart/cart';
import { pick } from '~/src/common/utils/object';
import { getCanonicalIdFromId } from '~/src/common/utils/product';
import { useGetCartFromCache } from '~/src/screens/CartScreen/hooks/useGetCartFromCache';
import { ATCSource } from '~/src/screens/CatalogContent/ContentScreen/types';

interface Params {
  product: UpdateCartProduct;
  onRefresh?: () => unknown;
  analytics: {
    categoryId?: string;
    categoryName?: string;
    eventSource: AddToCartProductEventSource;
    marketingPosition?: number;
    otherAnalyticsProperties?: ExtraCartEventProperties;
    source: ATCSource;
    subcategoryName?: string;
  };
}

/**
 * Ce hook intercept le cas ou les informations de livraisons sont manquante avant d'updater le cart
 * Si les informations sont manquantes, l'utilisateur verra s'afficher la modale correspondante
 * Une fois celle ci fermee, nous tentons de faire l'update a nouveau.
 */
export const useUpdateCartProductQuantityWithDelivery = (params: Params) => {
  const { mutateAsync } = useUpdateCartProductQuantity({
    product: params.product,
    ...pick(params.analytics, ['source', 'eventSource', 'categoryName', 'subcategoryName']),
  });

  const getCart = useGetCartFromCache();
  const requiredDelivery = useRequireDelivery();
  const handleFirstAddToCartError = useHandleFirstAddToCartError();

  const updateQuantityAsync = async (quantity: number) => {
    const cart = getCart();
    const previousQuantity = getCartQuantity(cart, params.product);

    const intercepted = requiredDelivery.intercept();

    // L'event est normalement envoye pendant la mutation mais si celle ci est delayee on envoie quand meme le click
    if (intercepted != null) {
      const delta = quantity - previousQuantity;

      Tracker.sendEvent(
        delta >= 0 ? 'click add to cart' : 'click remove product',
        getCartEventMapping({
          cart: cart,
          quantity: delta,
          source: params.analytics.source,
          product: params.product,
          status: intercepted.type === 'slot' ? 'need slot' : 'need shipping',
          otherAnalyticsProperties: params.analytics.otherAnalyticsProperties,
        }),
      );

      await intercepted.promise;
    }

    const result = await mutateAsync(
      {
        data: {
          product: {
            id: params.product.id,
            quantity,
            ...pick(params.analytics, ['categoryId', 'source', 'marketingPosition']),
          },
        },
      },
      { onError: handleFirstAddToCartError },
    );

    return { result, previousQuantity };
  };

  const removeProductAsync = () => {
    Tracker.sendEvent('click trash bin delete the product', {
      'product name': params.product.name,
      'product id': getCanonicalIdFromId(params.product.id),
      'cart stock status': 'en stock',
      'product event source': 'cart',
    });

    return mutateAsync({ data: { product: { id: params.product.id, quantity: 0 } } });
  };

  return {
    canUpdateCart: requiredDelivery.isValid,
    removeProductAsync,
    removeProduct: () => void removeProductAsync(),
    updateQuantity: (quantity: number) => void updateQuantityAsync(quantity),
    updateQuantityAsync,
  };
};
